import React, { useMemo, useState, useCallback } from "react";
import queries from "../helpers/queries";
import { withUser } from "../Contexts/user";
import { useQuery } from "@apollo/react-hooks";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import {
    FiX,
    FiCalendar,
    FiClock,
    FiMapPin,
    FiChevronLeft,
    FiChevronRight,
    FiDownload,
    FiTag,
} from "react-icons/fi";
import Modal from "../Components/modal";
import exportAgendaPdf from "../helpers/exportAgendaPdf";
import { toast } from "react-toastify";
import "react-big-calendar/lib/css/react-big-calendar.css";

const localizer = momentLocalizer(moment);

const formatDisplayDate = (value) => {
    if (!value) return "-";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const parseDateValue = (dateStr) => {
    if (!dateStr) return null;
    const parsed = moment(dateStr, ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601], true);
    return parsed.isValid() ? parsed : moment(dateStr);
};

const buildEventStart = (event) => {
    const base = parseDateValue(event.start);
    if (!base?.isValid()) return new Date();

    if (event.start_time) {
        const [hours, minutes] = event.start_time.split(":");
        base.hour(parseInt(hours, 10) || 0).minute(parseInt(minutes, 10) || 0).second(0);
        return base.toDate();
    }

    return base.startOf("day").toDate();
};

const buildEventEnd = (event) => {
    const endBase = parseDateValue(event.end || event.start);
    if (!endBase?.isValid()) return buildEventStart(event);

    if (event.end_time) {
        const [hours, minutes] = event.end_time.split(":");
        endBase.hour(parseInt(hours, 10) || 0).minute(parseInt(minutes, 10) || 0).second(0);
        return endBase.toDate();
    }

    if (event.start_time) {
        const start = moment(buildEventStart(event));
        return start.add(1, "hour").toDate();
    }

    return endBase.add(1, "day").startOf("day").toDate();
};

const formatTimeRange = (event) => {
    if (event.start_time && event.end_time) {
        return `${event.start_time} – ${event.end_time}`;
    }
    if (event.start_time) return event.start_time;
    if (event.end_time) return event.end_time;
    return null;
};

const formatDateRange = (event) => {
    const start = formatDisplayDate(event.start);
    const end = formatDisplayDate(event.end);
    if (!start || start === "-") return null;
    if (!end || end === "-" || start === end) return start;
    return `${start} – ${end}`;
};

const EVENT_LABEL_OPTIONS = [
    { value: "training", label: "Training Class" },
    { value: "trip", label: "Trip" },
    { value: "important", label: "Important Meeting" },
    { value: "event", label: "Event" },
    { value: "management", label: "Management Close" },
];

const EVENT_LABEL_COLORS = {
    training: { name: "blue", bg: "#3d7cff" },
    trip: { name: "yellow", bg: "#e6b422" },
    important: { name: "red", bg: "#e53935" },
    event: { name: "orange", bg: "#f5a623" },
    management: { name: "purple", bg: "#7c6cf0" },
};

const DEFAULT_EVENT_COLOR = { name: "gray", bg: "#6b7280" };

const getEventId = (event) => event._id || `${event.title}-${event.start}`;

const getEventColor = (event) =>
    EVENT_LABEL_COLORS[event?.label] || DEFAULT_EVENT_COLOR;

const formatEventLabel = (value) => {
    if (!value) return null;
    const match = EVENT_LABEL_OPTIONS.find((option) => option.value === value);
    return match ? match.label : value;
};

const hasEventImage = (event) => {
    const image = event?.image;
    if (!image || typeof image !== "string") return false;
    const trimmed = image.trim();
    return trimmed.length > 0 && trimmed !== "/event.jpg";
};

const formatLongSidebarDate = (date) =>
    moment(date).format("dddd, D MMMM, YYYY");

const formatEventClock = (timeStr) => {
    if (!timeStr) return null;
    const parsed = moment(timeStr, ["HH:mm", "H:mm", "hh:mm A"], true);
    return parsed.isValid() ? parsed.format("h:mm A") : timeStr;
};

const formatSidebarDetail = (event) => {
    const time = formatEventClock(event.start_time);
    const detail = formatEventLabel(event.label) || event.location || "Event";
    if (time) return `${time} : ${detail}`;
    return detail;
};

const isEventInMonth = (event, date) => {
    const monthStart = moment(date).startOf("month");
    const monthEnd = moment(date).endOf("month");
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);

    return (
        eventStart.isSameOrBefore(monthEnd, "day") &&
        eventEnd.isSameOrAfter(monthStart, "day")
    );
};

const isEventInYear = (event, date) => {
    const yearStart = moment(date).startOf("year");
    const yearEnd = moment(date).endOf("year");
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);

    return (
        eventStart.isSameOrBefore(yearEnd, "day") &&
        eventEnd.isSameOrAfter(yearStart, "day")
    );
};

const AGENDA_RANGE_DAYS = 30;

const getAgendaYearRange = (date = new Date()) => ({
    from: moment(date).startOf("year").format("YYYY-MM-DD"),
    to: moment(date).endOf("year").format("YYYY-MM-DD"),
});

const isEventInDateRange = (event, fromDate, toDate) => {
    const rangeStart = moment(fromDate).startOf("day");
    const rangeEnd = moment(toDate).endOf("day");
    if (!rangeStart.isValid() || !rangeEnd.isValid()) return false;

    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);

    return (
        eventStart.isSameOrBefore(rangeEnd, "day") &&
        eventEnd.isSameOrAfter(rangeStart, "day")
    );
};

const EventCard = ({ event, onSelectEvent }) => {
    const color = getEventColor(event);

    return (
        <button
            type="button"
            className={`calendar-event-card calendar-event-card--${color.name}`}
            style={{ backgroundColor: color.bg }}
            onClick={() => onSelectEvent(event)}
        >
            <div className="calendar-event-card__top">
                <span className="calendar-event-card__day">
                    {moment(event.start).format("D")}
                </span>
                <span className="calendar-event-card__title">
                    {event.title || "Untitled Event"}
                </span>
            </div>
            <div className="calendar-event-card__date">
                {formatLongSidebarDate(event.start)}
            </div>
            <div className="calendar-event-card__detail">
                {formatSidebarDetail(event)}
            </div>
        </button>
    );
};

const getEventsForDay = (events, date) =>
    events
        .filter((event) => moment(event.start).isSame(moment(date), "day"))
        .sort((a, b) => new Date(a.start) - new Date(b.start));

const CalendarDateHeader = ({
    label,
    date,
    drilldownView,
    onDrillDown,
    events = [],
}) => {
    const title = getEventsForDay(events, date)[0]?.title;

    const dayLabel = drilldownView ? (
        <a
            href="#"
            className="calendar-date-header__day"
            onClick={onDrillDown}
        >
            {label}
        </a>
    ) : (
        <span className="calendar-date-header__day">{label}</span>
    );

    return (
        <div className="calendar-date-header">
            {dayLabel}
            {title ? (
                <span className="calendar-date-header__title" title={title}>
                    {title}
                </span>
            ) : null}
        </div>
    );
};

const CalendarMonthEvent = ({ event, title }) => {
    const label = title || event.title || "Event";
    if (!hasEventImage(event)) {
        return <span className="calendar-event-title">{label}</span>;
    }

    return (
        <img
            className="calendar-event-thumb"
            src={event.image}
            alt={label}
            loading="lazy"
        />
    );
};

const EventsSidebar = ({ events, onSelectEvent, emptyMessage }) => (
    <aside className="calendar-sidebar">
        <div className="calendar-sidebar__header">
            <h2 className="calendar-sidebar__title">Event List</h2>
            <div className="calendar-event-badge">
                {events.length} event{events.length !== 1 ? "s" : ""}
            </div>
        </div>
        <div className="calendar-sidebar__list">
            {events.length === 0 ? (
                <p className="calendar-sidebar__empty">
                    {emptyMessage || "No events this month."}
                </p>
            ) : (
                events.map((event) => (
                    <EventCard
                        key={getEventId(event)}
                        event={event}
                        onSelectEvent={onSelectEvent}
                    />
                ))
            )}
        </div>
    </aside>
);

const EventsAgendaPanel = ({
    events,
    onSelectEvent,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    onExport,
    exporting,
}) => (
    <div className="calendar-agenda-panel">
        <div className="calendar-agenda-panel__toolbar">
            <div className="calendar-agenda-panel__filters">
                <label className="calendar-agenda-panel__filter">
                    <span className="calendar-agenda-panel__filter-label">From</span>
                    <input
                        type="date"
                        className="calendar-agenda-panel__date-input"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={(e) => onFromDateChange(e.target.value)}
                    />
                </label>
                <label className="calendar-agenda-panel__filter">
                    <span className="calendar-agenda-panel__filter-label">To</span>
                    <input
                        type="date"
                        className="calendar-agenda-panel__date-input"
                        value={toDate}
                        min={fromDate || undefined}
                        onChange={(e) => onToDateChange(e.target.value)}
                    />
                </label>
            </div>
            <button
                type="button"
                className="calendar-agenda-panel__export"
                onClick={onExport}
                disabled={exporting || events.length === 0}
            >
                <FiDownload aria-hidden="true" />
                {exporting ? "Exporting..." : "Export"}
            </button>
        </div>
        <div className="calendar-agenda-panel__list">
            {events.length === 0 ? (
                <p className="calendar-agenda-panel__empty">
                    No events in this date range.
                </p>
            ) : (
                events.map((event) => (
                    <EventCard
                        key={getEventId(event)}
                        event={event}
                        onSelectEvent={onSelectEvent}
                    />
                ))
            )}
        </div>
    </div>
);

const EventDetailModal = ({ events, activeTab, onTabChange, onClose }) => {
    const event = events[activeTab];
    if (!event) return null;

    const detailItems = [
        { icon: FiCalendar, label: "Date", value: formatDateRange(event) },
        { icon: FiClock, label: "Time", value: formatTimeRange(event) },
        { icon: FiMapPin, label: "Location", value: event.location },
        { icon: FiTag, label: "Label", value: formatEventLabel(event.label) },
    ].filter((item) => item.value && item.value !== "-");

    return (
        <Modal reset={onClose}>
            <div className="event-detail-modal">
                <div className="event-detail-modal__header">
                    <div className="event-detail-modal__heading">
                        <p className="event-detail-modal__eyebrow">Event Details</p>
                        <h2 className="event-detail-modal__title">
                            {event.title || "Event"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        className="event-detail-modal__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </div>

                {events.length > 1 && (
                    <div className="event-detail-modal__segment">
                        {events.map((item, index) => (
                            <button
                                key={item._id || `${item.title}-${index}`}
                                type="button"
                                className={
                                    activeTab === index
                                        ? "event-detail-modal__segment-option is-active"
                                        : "event-detail-modal__segment-option"
                                }
                                onClick={() => onTabChange(index)}
                            >
                                {item.title}
                            </button>
                        ))}
                    </div>
                )}

                {hasEventImage(event) && (
                    <img
                        className="event-detail-modal__image"
                        src={event.image}
                        alt={event.title || "Event"}
                    />
                )}

                {detailItems.length > 0 && (
                    <div className="event-detail-modal__details">
                        {detailItems.map(({ icon: Icon, label, value }) => (
                            <div className="event-detail-modal__detail" key={label}>
                                <div className="event-detail-modal__detail-icon">
                                    <Icon aria-hidden="true" />
                                </div>
                                <div className="event-detail-modal__detail-content">
                                    <div className="event-detail-modal__detail-label">
                                        {label}
                                    </div>
                                    <div className="event-detail-modal__detail-value">
                                        {value}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

const navigate = {
    PREVIOUS: "PREV",
    NEXT: "NEXT",
    TODAY: "TODAY",
};

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const YearMonthCell = ({
    monthDate,
    events,
    getNow,
    onDrillDown,
    onSelectEvent,
    onShowMore,
}) => {
    const monthStart = moment(monthDate).startOf("month");
    const daysInMonth = monthStart.daysInMonth();
    const startWeekday = monthStart.day();
    const today = moment(getNow ? getNow() : new Date());

    const days = [];
    for (let i = 0; i < startWeekday; i += 1) {
        days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        days.push(monthStart.clone().date(day));
    }

    const handleMonthClick = () => {
        if (onDrillDown) onDrillDown(monthStart.toDate(), "month");
    };

    const handleDayClick = (dayMoment) => {
        if (!dayMoment) return;
        const dayEvents = getEventsForDay(events, dayMoment.toDate());
        if (dayEvents.length === 0) {
            if (onDrillDown) onDrillDown(dayMoment.toDate(), "month");
            return;
        }
        if (dayEvents.length === 1 && onSelectEvent) {
            onSelectEvent(dayEvents[0]);
            return;
        }
        if (onShowMore) {
            onShowMore(dayEvents, dayMoment.toDate());
            return;
        }
        if (onSelectEvent) onSelectEvent(dayEvents[0]);
    };

    return (
        <div className="calendar-year-month">
            <button
                type="button"
                className="calendar-year-month__title"
                onClick={handleMonthClick}
            >
                {monthStart.format("MMMM")}
            </button>
            <div className="calendar-year-month__weekdays">
                {WEEKDAY_LABELS.map((label) => (
                    <span key={label} className="calendar-year-month__weekday">
                        {label}
                    </span>
                ))}
            </div>
            <div className="calendar-year-month__days">
                {days.map((dayMoment, index) => {
                    if (!dayMoment) {
                        return (
                            <span
                                key={`empty-${index}`}
                                className="calendar-year-month__day is-empty"
                            />
                        );
                    }

                    const dayEvents = getEventsForDay(events, dayMoment.toDate());
                    const isToday = dayMoment.isSame(today, "day");
                    const hasEvents = dayEvents.length > 0;
                    const firstColor = hasEvents
                        ? getEventColor(dayEvents[0]).bg
                        : null;

                    return (
                        <button
                            key={dayMoment.format("YYYY-MM-DD")}
                            type="button"
                            className={`calendar-year-month__day${
                                isToday ? " is-today" : ""
                            }${hasEvents ? " has-events" : ""}`}
                            onClick={() => handleDayClick(dayMoment)}
                            title={
                                hasEvents
                                    ? dayEvents
                                          .map((e) => e.title || "Event")
                                          .join(", ")
                                    : dayMoment.format("D MMMM YYYY")
                            }
                        >
                            <span className="calendar-year-month__day-num">
                                {dayMoment.date()}
                            </span>
                            {hasEvents && (
                                <span
                                    className="calendar-year-month__dot"
                                    style={{ backgroundColor: firstColor }}
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const YearView = ({
    date,
    events = [],
    getNow,
    onDrillDown,
    onSelectEvent,
    onShowMore,
}) => {
    const year = moment(date).year();
    const months = useMemo(
        () =>
            Array.from({ length: 12 }, (_, monthIndex) =>
                moment({ year, month: monthIndex, day: 1 }).toDate()
            ),
        [year]
    );

    return (
        <div className="rbc-year-view calendar-year-view">
            <div className="calendar-year-grid">
                {months.map((monthDate) => (
                    <YearMonthCell
                        key={moment(monthDate).format("YYYY-MM")}
                        monthDate={monthDate}
                        events={events}
                        getNow={getNow}
                        onDrillDown={onDrillDown}
                        onSelectEvent={onSelectEvent}
                        onShowMore={onShowMore}
                    />
                ))}
            </div>
        </div>
    );
};

YearView.range = (date) => {
    const start = moment(date).startOf("year").toDate();
    const end = moment(date).endOf("year").toDate();
    return { start, end };
};

YearView.navigate = (date, action) => {
    switch (action) {
        case navigate.PREVIOUS:
            return moment(date).subtract(1, "year").toDate();
        case navigate.NEXT:
            return moment(date).add(1, "year").toDate();
        default:
            return date;
    }
};

YearView.title = (date) => moment(date).format("YYYY");

const VIEW_LABELS = {
    month: "Month",
    year: "Yearly",
    agenda: "Agenda",
};

const CALENDAR_VIEWS = {
    month: true,
    year: YearView,
    agenda: true,
};

const CalendarToolbar = ({
    label,
    localizer,
    onNavigate,
    onView,
    view,
    views,
}) => {
    const messages = localizer.messages;
    const viewIndex = Math.max(views.indexOf(view), 0);

    return (
        <div className="rbc-toolbar">
            <div className="calendar-nav-segment">
                <button
                    type="button"
                    onClick={() => onNavigate(navigate.TODAY)}
                >
                    {messages.today}
                </button>
            </div>

            <div className="calendar-toolbar-month-nav">
                <button
                    type="button"
                    className="calendar-toolbar-month-nav__chevron"
                    onClick={(e) => {
                        onNavigate(navigate.PREVIOUS);
                        e.currentTarget.blur();
                    }}
                    aria-label={messages.previous}
                >
                    <FiChevronLeft aria-hidden="true" />
                </button>
                <span className="rbc-toolbar-label calendar-toolbar-month">{label}</span>
                <button
                    type="button"
                    className="calendar-toolbar-month-nav__chevron"
                    onClick={(e) => {
                        onNavigate(navigate.NEXT);
                        e.currentTarget.blur();
                    }}
                    aria-label={messages.next}
                >
                    <FiChevronRight aria-hidden="true" />
                </button>
            </div>

            {views.length > 1 && (
                <div
                    className="calendar-view-segment"
                    role="tablist"
                    aria-label="Calendar view"
                    style={{
                        "--segment-count": views.length,
                        "--segment-index": viewIndex,
                    }}
                >
                    <div className="calendar-view-segment__slider" aria-hidden="true" />
                    {views.map((name) => (
                        <button
                            key={name}
                            type="button"
                            role="tab"
                            aria-selected={view === name}
                            className={
                                view === name
                                    ? "calendar-view-segment__option is-active"
                                    : "calendar-view-segment__option"
                            }
                            onClick={() => onView(name)}
                        >
                            {VIEW_LABELS[name] || messages[name] || name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Events = () => {
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [calendarView, setCalendarView] = useState("month");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [agendaFrom, setAgendaFrom] = useState(
        () => getAgendaYearRange().from
    );
    const [agendaTo, setAgendaTo] = useState(() => getAgendaYearRange().to);
    const [exportingAgenda, setExportingAgenda] = useState(false);

    const { data: eventsData, loading } = useQuery(queries.FETCH_EVENTS_AGENT, {
        fetchPolicy: "network-only",
    });

    const calendarEvents = useMemo(() => {
        const events = eventsData?.fetchEventsAgent || [];
        return events.map((event) => ({
            ...event,
            start: buildEventStart(event),
            end: buildEventEnd(event),
        }));
    }, [eventsData]);

    const sidebarEvents = useMemo(() => {
        const filtered =
            calendarView === "year"
                ? calendarEvents.filter((event) =>
                      isEventInYear(event, currentDate)
                  )
                : calendarEvents.filter((event) =>
                      isEventInMonth(event, currentDate)
                  );
        return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
    }, [calendarEvents, currentDate, calendarView]);

    const agendaEvents = useMemo(() => {
        let from = agendaFrom;
        let to = agendaTo;
        if (moment(from).isAfter(moment(to), "day")) {
            from = agendaTo;
            to = agendaFrom;
        }
        return calendarEvents
            .filter((event) => isEventInDateRange(event, from, to))
            .sort((a, b) => new Date(a.start) - new Date(b.start));
    }, [calendarEvents, agendaFrom, agendaTo]);

    const agendaLengthDays = useMemo(() => {
        const from = moment(agendaFrom);
        const to = moment(agendaTo);
        if (!from.isValid() || !to.isValid()) return AGENDA_RANGE_DAYS;
        return Math.max(to.diff(from, "day"), 0) || AGENDA_RANGE_DAYS;
    }, [agendaFrom, agendaTo]);

    const handleAgendaFromChange = useCallback(
        (value) => {
            setAgendaFrom(value);
            if (value && moment(value).isValid()) {
                setCurrentDate(moment(value).toDate());
            }
            if (
                value &&
                agendaTo &&
                moment(value).isAfter(moment(agendaTo), "day")
            ) {
                setAgendaTo(value);
            }
        },
        [agendaTo]
    );

    const handleAgendaToChange = useCallback(
        (value) => {
            setAgendaTo(value);
            if (
                value &&
                agendaFrom &&
                moment(value).isBefore(moment(agendaFrom), "day")
            ) {
                setAgendaFrom(value);
                if (moment(value).isValid()) {
                    setCurrentDate(moment(value).toDate());
                }
            }
        },
        [agendaFrom]
    );

    const handleCalendarNavigate = useCallback(
        (date) => {
            setCurrentDate(date);
            if (calendarView !== "agenda") return;
            const span = agendaLengthDays;
            setAgendaFrom(moment(date).format("YYYY-MM-DD"));
            setAgendaTo(
                moment(date).add(span, "day").format("YYYY-MM-DD")
            );
        },
        [calendarView, agendaLengthDays]
    );

    const handleCalendarView = useCallback(
        (view) => {
            setCalendarView(view);
            if (view === "agenda") {
                const { from, to } = getAgendaYearRange(currentDate);
                setAgendaFrom(from);
                setAgendaTo(to);
            }
        },
        [currentDate]
    );

    const handleExportAgenda = useCallback(async () => {
        if (!agendaEvents.length) {
            toast.info("No events to export in this date range.");
            return;
        }
        setExportingAgenda(true);
        try {
            await exportAgendaPdf({
                events: agendaEvents,
                fromDate: agendaFrom,
                toDate: agendaTo,
                fileName: `events-agenda-${agendaFrom}_to_${agendaTo}`,
            });
            toast.success("Agenda PDF exported successfully");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to export agenda PDF");
        } finally {
            setExportingAgenda(false);
        }
    }, [agendaEvents, agendaFrom, agendaTo]);

    const calendarComponents = useMemo(
        () => ({
            toolbar: CalendarToolbar,
            ...(calendarView === "month"
                ? {
                      event: CalendarMonthEvent,
                      dateHeader: (props) => (
                          <CalendarDateHeader
                              {...props}
                              events={calendarEvents}
                          />
                      ),
                  }
                : {}),
        }),
        [calendarView, calendarEvents]
    );

    const eventStyleGetter = (event) => {
        const color = getEventColor(event);
        if (calendarView === "month" && hasEventImage(event)) {
            return {
                className: `calendar-event calendar-event--image calendar-event--${color.name}`,
                style: {
                    backgroundColor: "transparent",
                    border: `2px solid ${color.bg}`,
                },
            };
        }
        return {
            className: `calendar-event calendar-event--${color.name}`,
            style: {
                backgroundColor: color.bg,
                borderColor: color.bg,
            },
        };
    };

    const openEventsModal = (events) => {
        const list = Array.isArray(events) ? events : [events];
        setSelectedEvents(list);
        setActiveTab(0);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedEvents([]);
        setActiveTab(0);
    };

    return (
        <div className="calendar-page">
            <div className="calendar-container">
                <div className="calendar-page-header">
                    <div>
                        <h1 className="calendar-title">Events</h1>
                        <p className="calendar-subtitle">
                            View upcoming events and schedules
                        </p>
                    </div>
                </div>

                <div
                    className={`calendar-layout${
                        calendarView === "agenda" ? " calendar-layout--agenda" : ""
                    }`}
                >
                    <div
                        className={`calendar-main${
                            calendarView === "agenda" ? " calendar-main--agenda" : ""
                        }`}
                    >
                        {loading ? (
                            <div className="calendar-loading">
                                <div className="calendar-loading-spinner" aria-hidden="true" />
                                Loading events...
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`modern-calendar${
                                        calendarView === "agenda"
                                            ? " modern-calendar--agenda"
                                            : ""
                                    }${
                                        calendarView === "year"
                                            ? " modern-calendar--year"
                                            : ""
                                    }`}
                                >
                                    <BigCalendar
                                        popup={false}
                                        localizer={localizer}
                                        date={currentDate}
                                        onNavigate={handleCalendarNavigate}
                                        view={calendarView}
                                        onView={handleCalendarView}
                                        events={calendarEvents}
                                        views={CALENDAR_VIEWS}
                                        messages={{
                                            year: "Yearly",
                                        }}
                                        length={agendaLengthDays}
                                        components={calendarComponents}
                                        style={{
                                            height:
                                                calendarView === "agenda"
                                                    ? "auto"
                                                    : "calc(100vh - 240px)",
                                            minHeight:
                                                calendarView === "agenda"
                                                    ? 0
                                                    : "500px",
                                        }}
                                        onShowMore={(events) => openEventsModal(events)}
                                        onSelectEvent={(event) => openEventsModal(event)}
                                        eventPropGetter={eventStyleGetter}
                                    />
                                    {calendarView === "agenda" && (
                                        <EventsAgendaPanel
                                            events={agendaEvents}
                                            fromDate={agendaFrom}
                                            toDate={agendaTo}
                                            onFromDateChange={
                                                handleAgendaFromChange
                                            }
                                            onToDateChange={
                                                handleAgendaToChange
                                            }
                                            onExport={handleExportAgenda}
                                            exporting={exportingAgenda}
                                            onSelectEvent={(event) =>
                                                openEventsModal(event)
                                            }
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {(calendarView === "month" || calendarView === "year") &&
                        !loading && (
                        <EventsSidebar
                            events={sidebarEvents}
                            emptyMessage={
                                calendarView === "year"
                                    ? "No events this year."
                                    : "No events this month."
                            }
                            onSelectEvent={(event) => openEventsModal(event)}
                        />
                    )}
                </div>
            </div>

            {showModal && (
                <EventDetailModal
                    events={selectedEvents}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    onClose={closeModal}
                />
            )}
        </div>
    );
};

export default withUser(Events);
